// Angular
import { Component, OnDestroy, OnInit, Optional, ViewEncapsulation, ChangeDetectorRef, ViewRef } from '@angular/core';
import { Router } from '@angular/router';
// RxJS
import { Observable, Subscription } from 'rxjs';
// Object-Path
import * as objectPath from 'object-path';
// Layout
import { LayoutConfigService, MenuConfigService, PageConfigService } from '../../../core/_base/layout';
import { HtmlClassService } from '../html-class.service';
import { LayoutConfig } from '../../../core/_config/layout.config';
import { MenuConfig } from '../../../core/_config/menu.config';
import { PageConfig } from '../../../core/_config/page.config';
// User permissions
import { NgxPermissionsService } from 'ngx-permissions';
import { currentUserPermissions, Permission } from '../../../core/auth';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../core/reducers';

@Component({
  standalone: false,
    selector: 'kt-base',
    templateUrl: './base.component.html',
    styleUrls: ['./base.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class BaseComponent implements OnInit, OnDestroy {
    // Public variables
    selfLayout: string;
    asideDisplay: boolean;
    asideSecondary: boolean;
    subheaderDisplay: boolean;
    desktopHeaderDisplay: boolean;
    fitTop: boolean;
    fluid: boolean;

    // Private properties
    private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
    private currentUserPermissions$: Observable<Permission[]>;

    /**
     * Stores the date for today
     */
    public today: number;


    /**
     * Component constructor
     *
     * @param layoutConfigService: LayoutConfigService
     * @param menuConfigService: MenuConfifService
     * @param pageConfigService: PageConfigService
     * @param htmlClassService: HtmlClassService
     * @param store store app status
     * @param permissionsService PermissionsService
     * @param chRef ChangeDetectorRef class
     * @param router Router Service
     */
    constructor(
        private layoutConfigService: LayoutConfigService,
        private menuConfigService: MenuConfigService,
        private pageConfigService: PageConfigService,
        private htmlClassService: HtmlClassService,
        private store: Store<AppState>,
        @Optional() private permissionsService: NgxPermissionsService | null,
        private chRef: ChangeDetectorRef,
        public router: Router
    ) {
        this.today = Date.now();
        this.loadRolesWithPermissions();

        // register configs by demos
        this.layoutConfigService.loadConfigs(new LayoutConfig().configs);
        this.menuConfigService.loadConfigs(new MenuConfig().configs);
        this.pageConfigService.loadConfigs(new PageConfig().configs);

        // setup element classes
        this.htmlClassService.setConfig(this.layoutConfigService.getConfig());

        const subscr = this.layoutConfigService.onConfigUpdated$.subscribe(layoutConfig => {
            // Reset Metronic layout classes only. Clearing body.className removes ALL classes on <body>,
            // including any framework classes Angular may set on the document body.
            this.clearMetronicBodyClasses();
            this.htmlClassService.setConfig(layoutConfig);
        });
        this.unsubscribe.push(subscr);
    }

    /**
     * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
     */

    /**
     * On init
     */
    ngOnInit(): void {
        const config = this.layoutConfigService.getConfig();
        this.selfLayout = objectPath.get(config, 'self.layout');
        this.asideDisplay = objectPath.get(config, 'aside.self.display');
        this.subheaderDisplay = objectPath.get(config, 'subheader.display');
        this.desktopHeaderDisplay = objectPath.get(config, 'header.self.fixed.desktop');
        this.fitTop = objectPath.get(config, 'content.fit-top');
        this.fluid = objectPath.get(config, 'content.width') === 'fluid';

        // let the layout type change
        const subscr = this.layoutConfigService.onConfigUpdated$.subscribe(cfg => {
            setTimeout(() => {
                this.selfLayout = objectPath.get(cfg, 'self.layout');
                if (this.chRef && !(this.chRef as ViewRef).destroyed) {
                    this.chRef.detectChanges();
                }
            });
        });
        this.unsubscribe.push(subscr);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this.unsubscribe.forEach(sb => sb.unsubscribe());
    }

    /** Remove only Keen/Metronic kt-* classes from body; preserve Angular host and other app classes. */
    private clearMetronicBodyClasses(): void {
        Array.from(document.body.classList)
            .filter((c) => c.startsWith('kt-'))
            .forEach((c) => document.body.classList.remove(c));
    }

    /**
     * NGX Permissions, init roles
     */
    loadRolesWithPermissions() {
        this.currentUserPermissions$ = this.store.pipe(select(currentUserPermissions));
        const subscr = this.currentUserPermissions$.subscribe(res => {
            if (!res || res.length === 0) {
                return;
            }
            if (!this.permissionsService) {
                return;
            }

            const ngx = this.permissionsService;
            ngx.flushPermissions();
            res.forEach((pm: Permission) => ngx.addPermission(pm.name));
        });
        this.unsubscribe.push(subscr);
    }
}
